import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Select from "react-select";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import useDarkMode from "../../hooks/useDarkMode";
import DropDownStyles, { DropDownStylesDark, defaultSelectPortalProps } from "../staff/dropdownStyles";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";

const currencySymbols = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  PKR: "₨",
  Rs: "₨",
};

const getCurrencySymbol = (currencyCode) => currencySymbols[currencyCode] || currencyCode || "£";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const CashReport = () => {
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { getCompCurrency } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().startOf("month").toDate());
  const [endDate, setEndDate] = useState(moment().endOf("month").toDate());
  const [staffOptions, setStaffOptions] = useState([{ value: "all", label: "All Staff" }]);
  const [tillOptions, setTillOptions] = useState([{ value: "all", label: "All Tills" }]);
  const [selectedStaff, setSelectedStaff] = useState({ value: "all", label: "All Staff" });
  const [selectedTill, setSelectedTill] = useState({ value: "all", label: "All Tills" });
  const [selectedCashType, setSelectedCashType] = useState({ value: "all", label: "All Cash Types" });

  const [summary, setSummary] = useState({
    totalPettyCash: 0,
    openingBalance: 0,
    totalDeposits: 0,
    totalWithdrawals: 0,
    floatClosingBalance: 0,
    netCashMovement: 0,
    transactionCount: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [pettyCashByHead, setPettyCashByHead] = useState([]);
  const [floatCashByType, setFloatCashByType] = useState([]);

  const symbol = useMemo(() => getCurrencySymbol(compCurrency), [compCurrency]);
  const cashTypeOptions = useMemo(
    () => [
      { value: "all", label: "All Cash Types" },
      { value: "petty", label: "Petty Cash" },
      { value: "float", label: "Float Cash" },
    ],
    []
  );

  const formatCurrency = useCallback(
    (value) =>
      `${symbol}${toNumber(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [symbol]
  );

  const fetchCashReports = useCallback(async () => {
    if (!CompID || !startDate || !endDate) return;

    setLoading(true);
    try {
      const params = {
        CompID,
        StartDate: moment(startDate).format("YYYY-MM-DD"),
        EndDate: moment(endDate).format("YYYY-MM-DD"),
        StaffName: selectedStaff?.value !== "all" ? selectedStaff?.value : "",
        TillName: selectedTill?.value !== "all" ? selectedTill?.value : "",
        CashType:
          selectedCashType?.value === "petty"
            ? "Petty"
            : selectedCashType?.value === "float"
            ? "Float"
            : "All",
      };

      const response = await axiosInstance.get(APIs.GET_CASH_REPORT_WITH_FILTERS, { params });
      const payload = response?.data?.data || {};

      if (response?.data?.status) {
        const reportSummary = payload?.summary || payload?.Summary || {};
        const reportTransactions = payload?.transactions || payload?.Transactions || [];
        const reportPettyByHead = payload?.pettyCashByHead || payload?.PettyCashByHead || [];
        const reportFloatByType = payload?.floatCashByType || payload?.FloatCashByType || [];
        const reportStaffOptions = payload?.staffOptions || payload?.StaffOptions || [];
        const reportTillOptions = payload?.tillOptions || payload?.TillOptions || [];

        setSummary({
          totalPettyCash: toNumber(reportSummary?.totalPettyCash || reportSummary?.TotalPettyCash),
          openingBalance: toNumber(reportSummary?.openingBalance || reportSummary?.OpeningBalance),
          totalDeposits: toNumber(reportSummary?.totalDeposits || reportSummary?.TotalDeposits),
          totalWithdrawals: toNumber(reportSummary?.totalWithdrawals || reportSummary?.TotalWithdrawals),
          floatClosingBalance: toNumber(reportSummary?.floatClosingBalance || reportSummary?.FloatClosingBalance),
          netCashMovement: toNumber(reportSummary?.netCashMovement || reportSummary?.NetCashMovement),
          transactionCount: toNumber(reportSummary?.transactionCount || reportSummary?.TransactionCount),
        });

        setTransactions(Array.isArray(reportTransactions) ? reportTransactions : []);
        setPettyCashByHead(Array.isArray(reportPettyByHead) ? reportPettyByHead : []);
        setFloatCashByType(Array.isArray(reportFloatByType) ? reportFloatByType : []);

        const normalizedStaffOptions = [
          { value: "all", label: "All Staff" },
          ...reportStaffOptions
            .filter((item) => typeof item === "string" && item.trim() !== "")
            .map((item) => ({ value: item, label: item })),
        ];
        const normalizedTillOptions = [
          { value: "all", label: "All Tills" },
          ...reportTillOptions
            .filter((item) => typeof item === "string" && item.trim() !== "")
            .map((item) => ({ value: item, label: item })),
        ];

        setStaffOptions(normalizedStaffOptions);
        setTillOptions(normalizedTillOptions);

        if (!normalizedStaffOptions.some((item) => item.value === selectedStaff?.value)) {
          setSelectedStaff({ value: "all", label: "All Staff" });
        }
        if (!normalizedTillOptions.some((item) => item.value === selectedTill?.value)) {
          setSelectedTill({ value: "all", label: "All Tills" });
        }
      } else {
        setSummary({
          totalPettyCash: 0,
          openingBalance: 0,
          totalDeposits: 0,
          totalWithdrawals: 0,
          floatClosingBalance: 0,
          netCashMovement: 0,
          transactionCount: 0,
        });
        setTransactions([]);
        setPettyCashByHead([]);
        setFloatCashByType([]);
      }
    } catch (error) {
      showErrorToast("Failed to load cash reports");
      setSummary({
        totalPettyCash: 0,
        openingBalance: 0,
        totalDeposits: 0,
        totalWithdrawals: 0,
        floatClosingBalance: 0,
        netCashMovement: 0,
        transactionCount: 0,
      });
      setTransactions([]);
      setPettyCashByHead([]);
      setFloatCashByType([]);
    } finally {
      setLoading(false);
    }
  }, [CompID, startDate, endDate, selectedStaff, selectedTill, selectedCashType]);

  useEffect(() => {
    fetchCashReports();
  }, [fetchCashReports]);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Petty Cash Report</li>
          </ol>
          <h1 className="page-header mb-0">Petty Cash Report</h1>
        </div>
      </div>

      <div className="mb-4">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Petty Cash Report"
          totalCount={summary.transactionCount}
          topFilters={
            <>
              <ModernGridToolbarFilterPill iconClass="fa fa-calendar" wide>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  placeholder="Select Date Range"
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-user">
                <Select
                  value={selectedStaff}
                  onChange={setSelectedStaff}
                  options={staffOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Staff"
                  isSearchable
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-cash-register">
                <Select
                  value={selectedTill}
                  onChange={setSelectedTill}
                  options={tillOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Tills"
                  isSearchable
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
              <ModernGridToolbarFilterPill iconClass="fa fa-filter">
                <Select
                  value={selectedCashType}
                  onChange={setSelectedCashType}
                  options={cashTypeOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Cash Types"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            </>
          }
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="260px">
        <div className="row g-3 mb-3">
          <div className="col-xl-3 col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Total Petty Cash</div>
                <h4 className="mb-0">{formatCurrency(summary.totalPettyCash)}</h4>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Float Opening Balance</div>
                <h4 className="mb-0">{formatCurrency(summary.openingBalance)}</h4>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Float Total Deposits</div>
                <h4 className="mb-0">{formatCurrency(summary.totalDeposits)}</h4>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Float Total Withdrawals</div>
                <h4 className="mb-0">{formatCurrency(summary.totalWithdrawals)}</h4>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="text-muted small">Float Closing Balance</div>
                <h4 className="mb-0">{formatCurrency(summary.floatClosingBalance)}</h4>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-xl-6">
            <div className="card">
              <div className="card-header">
                <strong>Petty Cash Breakdown by Head</strong>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Head</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pettyCashByHead.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center py-3">
                            No petty cash entries found for selected filters
                          </td>
                        </tr>
                      ) : (
                        pettyCashByHead.map((item, index) => (
                          <tr key={`${item.headName || item.HeadName}-${index}`}>
                            <td>{item.headName || item.HeadName || "-"}</td>
                            <td className="text-end">
                              {formatCurrency(item.amount || item.Amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-6">
            <div className="card">
              <div className="card-header">
                <strong>Float Cash Breakdown by Transaction Type</strong>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Transaction Type</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {floatCashByType.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center py-3">
                            No float cash entries found for selected filters
                          </td>
                        </tr>
                      ) : (
                        floatCashByType.map((item, index) => (
                          <tr key={`${item.transactionType || item.TransactionType}-${index}`}>
                            <td>{item.transactionType || item.TransactionType || "-"}</td>
                            <td className="text-end">
                              {formatCurrency(item.amount || item.Amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <strong>Transactions ({summary.transactionCount})</strong>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Date Time</th>
                        <th>Cash Type</th>
                        <th>Expense Type</th>
                        <th>Staff</th>
                        <th>Till</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-3">
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        transactions.map((item, index) => (
                          <tr key={`${item.pce_ID || item.PCE_ID}-${index}`}>
                            <td>{moment(item.pCDateTime || item.PCDateTime).format("DD MMM YYYY, HH:mm")}</td>
                            <td>{item.cashType || item.CashType || "-"}</td>
                            <td>{item.expenType || item.ExpenType || "-"}</td>
                            <td>{item.staffName || item.StaffName || "-"}</td>
                            <td>{item.tillName || item.TillName || "-"}</td>
                            <td className="text-end">
                              {formatCurrency(item.amount || item.Amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default CashReport;
